import React, { FC } from 'react';

import { DEFAULT_LOCALE, normalizeLocale } from '@rover/constants/src/i18n/locale';
import { Box } from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import type { CountryConfiguration } from '@rover/rsdk/src/apiClient/latest/model/countryConfiguration';
import type { FrontendConfiguration } from '@rover/rsdk/src/apiClient/latest/model/frontendConfiguration';
import type { GroomingSearchResponse } from '@rover/rsdk/src/apiClient/latest/model/groomingSearchResponse';
import type { SeoLocation } from '@rover/rsdk/src/apiClient/latest/model/seoLocation';
import type { PetType } from '@rover/types/src/Pet';
import type { SeoLocationDescriptor } from '@rover/utilities/seo';

import AppStoreCallout from '../../../../shared/AppStoreCallout/AppStoreCallout';
import LandingReviewsCarousel from '../../../components/LandingReviewsCarousel';
import QuickFact from '../../../components/QuickFact';
import type { Review } from '../../../components/ReviewsCarousel';
import RoverProtectCondensed from '../../../ServiceLanding/components/RoverProtectCondensed';
import SeoLocationFaqList from '../../components/SeoLocationFaqList';
import SeoLocationFooterLinks from '../../components/SeoLocationFooterLinks';
import SeoLocationPageWrapper from '../../components/SeoLocationPageWrapper';
import SeoLocationRoverProtect from '../../components/SeoLocationRoverProtect';
import { PageSectionId, useSectionRef } from '../../hooks/useSectionRef';

import GroomingHowBrandWorks from './components/GroomingHowBrandWorks';
import GroomingSeoLocationHero from './components/GroomingSeoLocationHero';
import GroomingSeoLocationResults from './components/GroomingSeoLocationResults';
import { getGroomingSearchPageUrl, getGroomingSearchParams } from './utils/groomingSearch';
import { appShowcaseGroomingImage, groomingQuickFactSticker } from './images';

export type GroomingSeoLocationPageProps = {
  seoLocation: SeoLocation;
  seoLocationDescriptor: SeoLocationDescriptor;
  reviews: Review[];
  countryConfig: Pick<
    CountryConfiguration,
    'urls' | 'currencyCode' | 'hideBackgroundCheckVerbiage' | 'formattedVetCareReimbursementAmount'
  >;
  frontendConfig: {
    settings: Pick<FrontendConfiguration['settings'], 'language'>;
  };
  groomingSearchResponse?: GroomingSearchResponse;
};

const MIN_REVIEWS_TO_SHOW_CAROUSEL = 3;

const MAX_REVIEWS = 7;

const GroomingSeoLocationPageTemplate: FC<GroomingSeoLocationPageProps> = ({
  seoLocation,
  seoLocationDescriptor,
  reviews,
  countryConfig,
  frontendConfig,
  groomingSearchResponse,
}) => {
  const { details, linkedLocations, localityType } = seoLocation;
  const {
    breadcrumbData,
    cityName,
    internalServiceType,
    name: locationName,
    petType,
    relatedServices,
  } = details;
  const locale = normalizeLocale(frontendConfig.settings.language) ?? DEFAULT_LOCALE;
  const reviewsSectionRef = useSectionRef(PageSectionId.ReviewsCarousel);
  const searchParams = getGroomingSearchParams(seoLocation);
  const searchPageUrl = getGroomingSearchPageUrl(searchParams);
  const serviceTypeName = details.serviceTypeName.toLocaleLowerCase(locale);
  const titleCasedServiceKeyword = details.titleCasedServiceKeyword || details.serviceTypeName;
  // The footer reads "Rover also offers <service> in:", so it wants the sentence-cased
  // keyword. Grooming has no sentence-cased keyword yet, so fall back to the lower-cased
  // title keyword rather than rendering the service name title-cased mid-sentence.
  const footerServiceName = details.sentenceCasedServiceKeyword || serviceTypeName;

  return (
    <SeoLocationPageWrapper seoLocation={seoLocation} countryConfig={countryConfig}>
      <Section>
        <GroomingSeoLocationHero details={details} searchParams={searchParams} />
      </Section>

      <RoverProtectCondensed countryConfig={countryConfig} isGrooming my={['6x', '12x']} />

      {groomingSearchResponse && (
        <GroomingSeoLocationResults
          heading={`Find ${serviceTypeName} in ${locationName}`}
          searchResults={groomingSearchResponse.results}
          otherGroomers={groomingSearchResponse.otherGroomers}
          searchPageUrl={searchPageUrl}
          currencyCode={countryConfig.currencyCode}
          locale={locale}
        />
      )}

      <QuickFact
        themeColor="green"
        stickerImageSrc={groomingQuickFactSticker}
        stickerBottom="-28px"
        my="0x"
      >
        A private appointment, just for your dog, at your home or your groomer&rsquo;s.
      </QuickFact>

      <Box py={['16x', '12x']}>
        {reviews.length >= MIN_REVIEWS_TO_SHOW_CAROUSEL && (
          <LandingReviewsCarousel
            htmlRef={reviewsSectionRef}
            reviews={reviews.slice(0, MAX_REVIEWS)}
            petType={(petType as PetType) ?? undefined}
            locationName={locationName}
            internalServiceTypeSlug={internalServiceType.slug}
            getCardHref={(review) => review.providerProfileUrl}
            cardHrefLabel="View Profile"
            ctaHref={searchPageUrl}
          />
        )}
      </Box>

      <SeoLocationRoverProtect isGrooming />

      <GroomingHowBrandWorks />

      <QuickFact themeColor="blue" mt="0x">
        Pet parents rate Rover groomers 4.9 stars on average.
      </QuickFact>

      <SeoLocationFaqList
        seoLocationDescriptor={seoLocationDescriptor}
        localityType={localityType}
        titleCasedServiceKeyword={titleCasedServiceKeyword}
        locationName={locationName}
      />

      <Section mt={['8x', '16x']}>
        <AppStoreCallout altImageSrc={appShowcaseGroomingImage} imageMaxWidth="50%" />
      </Section>

      <SeoLocationFooterLinks
        breadcrumbData={breadcrumbData}
        serviceName={footerServiceName}
        locationName={cityName}
        linkedLocations={linkedLocations}
        relatedServices={relatedServices}
        internalServiceTypeSlug={internalServiceType.slug}
      />
    </SeoLocationPageWrapper>
  );
};

export default GroomingSeoLocationPageTemplate;
